/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {AfterContentChecked, Component, Input, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {WizardStepComponent} from '../../../shared/wizard/wizard-step.component';
import {WizardIconEnum} from '../../../shared/wizard/wizard-icon.enum';
import {WizardService} from '../../../shared/wizard/wizard.service';
import {StepPositionDetailModel} from './step-position-detail.model';
import {filter} from 'rxjs/operators';
import {Subscription} from 'rxjs';
import {ReferenceDataApi} from '../../../api/ms-claims/reference-data.api';
import {MaterialPositionTableService} from '../../shared/material-position-table/service/material-position-table.service';
import {StepPositionDetailMaterialModel} from './step-position-detail-material.model';
import {ClaimPositionDetailModel} from '../../../api/ms-claims/contracts/claim-position-detail.model';
import {MaterialPositionTableComponent} from '../../shared/material-position-table/material-position-table.component';
import {ClaimsApi} from '../../../api/ms-claims/claims.api';

@Component({
    selector: 'app-step-position-detail',
    templateUrl: './step-position-detail.component.html',
    styleUrls: ['./step-position-detail.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class StepPositionDetailComponent extends WizardStepComponent implements OnInit, OnDestroy, AfterContentChecked {
    @Input() stepIndex: number;
    @ViewChild('positionTable', {static: true}) positionTable: MaterialPositionTableComponent;
    public material: StepPositionDetailMaterialModel;
    stepModel = {
        canSave: true,
        canSend: false,
        icon: WizardIconEnum.NONE,
    } as StepPositionDetailModel;
    /** Variables to pass data to position modal **/
    public partnerCode: string;
    public supplyingPackingPlantCode: string;
    public claimCode: string;

    public maxNumberOfPositions = 0;
    private subscriptions = new Subscription();

    constructor(protected wizardService: WizardService,
                private claimReferenceApi: ReferenceDataApi,
                private positionService: MaterialPositionTableService,
                private claimsApi: ClaimsApi) {
        super(wizardService);
        wizardService.observable
                .pipe(filter(step => step.index === 0
                        && step.data != null
                        && !this.isDeepEmpty(step.data.material)))
                .subscribe(value => {
                    this.material = value.data.material;
                });
    }

    ngAfterContentChecked(): void {
        this.sendUpdateForm();
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.getPositionModalData();
        this.getClaimConfig();

        this.subscriptions.add(
                this.positionTable.isValidForSave.subscribe((canSave: boolean) => {
                    this.stepModel.canSave = canSave;
                    this.manageStepIcon();
                }));

        this.subscriptions.add(
                this.positionService.positionData.subscribe((positions: ClaimPositionDetailModel[]) => {
                    this.stepModel.isTouched = true;
                    this.stepModel.data = positions;
                    this.stepModel.canSend = this.isValidForSend(positions);
                    this.manageStepIcon();
                    this.manageStepIcon();
                }));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    getPositionModalData(): void {
        this.subscriptions.add(
                this.wizardService.observable.pipe(filter(step => step.index === 0
                        && step.data != null
                        && step.data.partner != null
                        && step.data.supplyingPackingPlant != null
                        && step.data.supplyingPackingPlant.code != null
                        && step.data.claimCode != null
                        && step.data.claimCode.code != null
                )).subscribe((value) => {
                    this.partnerCode = value.data.partner.code;
                    this.supplyingPackingPlantCode = value.data.supplyingPackingPlant.code;
                    this.claimCode = value.data.claimCode.code;
                }));
    }

    private getClaimConfig(): void {
        this.subscriptions.add(this.claimsApi.getClaimConfig().subscribe(config => {
            this.maxNumberOfPositions = config.maxNumberOfPositions;
        }));
    }

    private manageStepIcon(): void {
        if (this.stepModel.canSend && this.stepModel.canSave) {
            this.stepModel.icon = WizardIconEnum.SUCCESS;
        } else {
            this.stepModel.icon = WizardIconEnum.ERROR;
        }
    }

    private isValidForSend(positions: ClaimPositionDetailModel[]): boolean {
        let isValidPosition = true;
        if (positions.length > 0) {
            positions.forEach(position => {
                if (!(position.quantity != null
                        && position.decision != null
                        && position.decision.code != null
                        && (!position.decision.hasTimeEffort || position.timeEffort != null)
                        && position.claimError != null
                        && position.claimError.code != null
                        && position.businessCategory != null
                        && position.businessCategory.code != null
                        && position.handlingUnit != null
                        && position.handlingUnit.length > 0)) {
                    isValidPosition = false;
                    return;
                }
            });
        } else {
            isValidPosition = false;
        }

        return isValidPosition;
    }
}
